import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Theme persistence + system preference following.
 *
 * Three themes:
 *   - 'light'  — Aurora default
 *   - 'dark'   — Aurora dark
 *   - 'cb'     — colour-blind safe (deuteranopia-tuned)
 *
 * Storage:
 *   - 'system' (or absent) means "follow prefers-color-scheme" and we
 *     resolve dynamically. Picking light/dark/cb explicitly stores
 *     that value and stops following the OS.
 *
 * Hydrate-before-render: applyResolvedTheme() is called from main.tsx
 * before ReactDOM.render so the first paint already has the right
 * data-theme attribute — no flash of wrong colours. This mirrors the
 * pattern in Sidebar.tsx for the nav-collapsed state.
 */

export type Theme = 'light' | 'dark' | 'cb';
export type ThemeChoice = Theme | 'system';

const STORAGE_KEY = 'hcc:ui:theme';

/** Pre-PR-5 the toggle is hidden behind a separate flag so we can
 * land the plumbing without committing to the user-facing control.
 * Flip via DevTools: `localStorage.setItem('hcc:ui:dev', '1')`. */
const DEV_FLAG_KEY = 'hcc:ui:dev';

export function isThemeTogglerEnabled(): boolean {
  try {
    return window.localStorage.getItem(DEV_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function readStoredChoice(): ThemeChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'cb' || raw === 'system') {
      return raw;
    }
  } catch {
    // localStorage can throw in private browsing; fall through.
  }
  return 'system';
}

function writeStoredChoice(choice: ThemeChoice) {
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Best-effort persistence.
  }
}

export function resolveSystemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveTheme(choice: ThemeChoice): Theme {
  return choice === 'system' ? resolveSystemTheme() : choice;
}

/** Apply theme to the document. Idempotent and cheap; safe to call
 * from main.tsx, useEffect, or matchMedia listeners. */
export function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

/** Hydrate before React mounts. Called from main.tsx. */
export function bootstrapTheme(): Theme {
  const theme = resolveTheme(readStoredChoice());
  applyTheme(theme);
  return theme;
}

// ---- Reactive store -------------------------------------------------
//
// We use useSyncExternalStore so the hook resyncs when:
//   - the user picks a theme in the toggle (write to store + emit)
//   - the system preference changes (matchMedia listener)
//   - another tab changes localStorage (storage event)

type Listener = () => void;
const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

let mediaListenerAttached = false;
function attachMediaListener() {
  if (mediaListenerAttached || typeof window === 'undefined' || !window.matchMedia) {
    return;
  }
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    // Only react if the user is on 'system' — explicit choices win.
    if (readStoredChoice() === 'system') {
      applyTheme(resolveSystemTheme());
      emit();
    }
  };
  // Older Safari uses addListener; modern uses addEventListener.
  if (mql.addEventListener) mql.addEventListener('change', onChange);
  else mql.addListener(onChange);
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      applyTheme(resolveTheme(readStoredChoice()));
      emit();
    }
  });
  mediaListenerAttached = true;
}

function subscribe(listener: Listener) {
  attachMediaListener();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ThemeChoice {
  return readStoredChoice();
}

function getServerSnapshot(): ThemeChoice {
  return 'system';
}

export interface UseThemeResult {
  /** The user's persisted choice — 'system' means "follow OS". */
  choice: ThemeChoice;
  /** The currently-applied concrete theme. */
  resolved: Theme;
  /** Set the choice. Pass 'system' to clear an explicit pick. */
  setChoice: (next: ThemeChoice) => void;
}

export function useTheme(): UseThemeResult {
  const choice = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const resolved = resolveTheme(choice);

  // If the user is on 'system' and the OS happens to swap *while the
  // tab was hidden*, the snapshot itself doesn't change but the
  // resolved theme might be wrong. Resync on mount and when choice
  // changes.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    writeStoredChoice(next);
    applyTheme(resolveTheme(next));
    emit();
  }, []);

  return { choice, resolved, setChoice };
}
